import type { Message, ProductCard, RichContent } from '../types/messages';
import type { WidgetStrings } from '../i18n/types';
import { MESSAGE_TYPES } from '../constants';
import { formatWidgetString } from '../utils/i18n';
import { isSafeUrl } from '../utils/urlSafety';

/** Button value prefix for the "Yes" (go to checkout) confirmation button. */
export const CART_CHECKOUT_STARTED_PREFIX = '__checkout_started__|';
/** Button value prefix for the "No" (stay in chat, offer product help) confirmation button. */
export const CART_CHECKOUT_NO_PREFIX = '__cart_checkout_no__|';
/** Button value for the "Keep shopping" confirmation button. */
export const CART_KEEP_SHOPPING_ACTION = '__cart_keep_shopping__';

export type CartConfirmationData = {
  quantity: number;
  subtotal?: string;
  total?: string;
};

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function formatCartAmount(value: unknown, currency = 'USD'): string {
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (typeof value !== 'number' || !Number.isFinite(value)) return '';
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(value / 100);
  } catch {
    return `$${(value / 100).toFixed(2)}`;
  }
}

export function getCartConfirmationData(
  payload: Record<string, unknown> | null | undefined,
  quantity = 1
): CartConfirmationData {
  const cart = isPlainRecord(payload?.cart) ? payload.cart : payload || {};
  const currency =
    typeof cart.currency === 'string'
      ? cart.currency
      : typeof payload?.currency === 'string'
        ? payload.currency
        : 'USD';
  const subtotal = formatCartAmount(
    cart.items_subtotal_price ?? cart.subtotal_price ?? cart.subtotal,
    currency
  );
  const total = formatCartAmount(cart.total_price ?? cart.total, currency);
  return {
    quantity,
    ...(subtotal ? { subtotal } : {}),
    ...(total ? { total } : {}),
  };
}

/** Products shown only after a successful add-to-cart. */
export function getComplementaryProducts(product: ProductCard): ProductCard[] {
  return Array.isArray(product.complementaryProducts) ? product.complementaryProducts : [];
}

/** Generic follow-up questions for cart help topics when the merchant preset has no canned reply. */
export function genericCartHelpQuestion(
  strings: WidgetStrings,
  topic: string,
  productTitle: string
): string | undefined {
  if (topic === 'apply') {
    return formatWidgetString(strings.cartHelpApplyQuestion, { item: productTitle });
  }
  if (topic === 'ingredients') {
    return formatWidgetString(strings.cartHelpIngredientsQuestion, { item: productTitle });
  }
  return undefined;
}

export interface CartConfirmationInput {
  product: ProductCard;
  checkoutUrl?: string | null;
  /** Cart strategy that performed the add (`shopify_ajax` | `api`), echoed in the checkout button. */
  strategy?: string;
  cartData?: CartConfirmationData;
  /** Discount code that was actually applied to the cart, if any. */
  discountCode?: string;
  strings: WidgetStrings;
  now: Date;
}

/**
 * Builds the local-only assistant messages shown after an item was added to the cart:
 * the confirmation (with Keep shopping, or Yes/No checkout buttons) and, when the product
 * carries complementary products, a second product-cards message.
 */
export function buildCartConfirmationMessages({
  product,
  checkoutUrl,
  strategy,
  cartData = { quantity: 1 },
  discountCode,
  strings,
  now,
}: CartConfirmationInput): Message[] {
  const safeCheckoutUrl = checkoutUrl && isSafeUrl(checkoutUrl) ? checkoutUrl : '';
  const selectedVariantLabel = String(product.selectedVariantLabel || '').trim();
  const productTitle = String(product.title || '').trim() || strings.thatItem;
  const addedItemTitle = selectedVariantLabel || productTitle;
  const quantity = Math.max(1, Math.floor(cartData.quantity || 1));
  const totalLabel = cartData.total
    ? formatWidgetString(strings.cartUpdatedTotal, { total: cartData.total })
    : cartData.subtotal
      ? formatWidgetString(strings.cartUpdatedSubtotal, { subtotal: cartData.subtotal })
      : '';
  const checkoutLabel = safeCheckoutUrl
    ? formatWidgetString(strings.cartCheckoutLink, { url: safeCheckoutUrl })
    : strings.cartCheckoutFromCart;
  const buttons: NonNullable<RichContent['buttons']> = [];
  const shouldContinueShopping = Boolean(discountCode || product.continueShoppingAfterAdd);
  if (shouldContinueShopping) {
    buttons.push({
      label: strings.keepShopping,
      value: CART_KEEP_SHOPPING_ACTION,
    });
  } else if (safeCheckoutUrl) {
    buttons.push({
      label: strings.yes,
      value:
        CART_CHECKOUT_STARTED_PREFIX +
        [
          encodeURIComponent(safeCheckoutUrl),
          encodeURIComponent(String(product.id || '')),
          encodeURIComponent(addedItemTitle),
          encodeURIComponent(strategy || ''),
        ].join('|'),
      href: safeCheckoutUrl,
      target: '_self',
    });
  }
  if (!shouldContinueShopping) {
    buttons.push({
      label: strings.no,
      value: `${CART_CHECKOUT_NO_PREFIX}${encodeURIComponent(productTitle)}`,
    });
  }

  const messages: Message[] = [
    {
      id: `cart-confirmation-${product.id || 'product'}-${now.getTime()}`,
      sender: 'ai',
      text: [
        formatWidgetString(strings.cartAdded, {
          item: `${productTitle}${selectedVariantLabel ? ` — ${selectedVariantLabel}` : ''}`,
        }),
        formatWidgetString(strings.cartQuantity, { count: quantity }),
        discountCode ? formatWidgetString(strings.cartDiscountApplied, { code: discountCode }) : '',
        totalLabel,
        shouldContinueShopping ? strings.cartKeepShoppingHint : checkoutLabel,
        shouldContinueShopping ? '' : strings.cartReadyForCheckout,
      ]
        .filter(Boolean)
        .join(' '),
      timestamp: now.toISOString(),
      status: 'delivered',
      localOnly: true,
      ...(buttons.length > 0
        ? {
            type: MESSAGE_TYPES.BUTTONS,
            richContent: {
              buttonStyle: 'pill',
              buttons,
            },
          }
        : null),
    },
  ];

  const complementaryProducts = getComplementaryProducts(product);
  if (complementaryProducts.length > 0) {
    messages.push({
      id: `cart-complements-${product.id || 'product'}-${now.getTime()}`,
      sender: 'ai',
      text:
        product.complementaryText ||
        formatWidgetString(strings.complementaryText, { item: productTitle }),
      timestamp: now.toISOString(),
      status: 'delivered',
      localOnly: true,
      type: MESSAGE_TYPES.PRODUCT_CARDS,
      richContent: {
        headline: product.complementaryHeadline || strings.complementaryHeadline,
        layout: 'scroll',
        products: complementaryProducts,
      },
    });
  }

  return messages;
}

export interface CartErrorInput {
  product: ProductCard;
  productUrl?: string | null;
  strings: WidgetStrings;
  now: Date;
  unconfirmed?: boolean;
}

/** Builds the assistant message shown when an add-to-cart could not be completed from chat. */
export function buildCartErrorMessage({
  product,
  productUrl,
  strings,
  now,
  unconfirmed = false,
}: CartErrorInput): Message {
  const safeProductUrl = productUrl && isSafeUrl(productUrl) ? productUrl : '';
  const buttons: NonNullable<RichContent['buttons']> = [];
  if (safeProductUrl) {
    buttons.push({
      label: strings.viewProduct,
      href: safeProductUrl,
      target: '_blank',
    });
  }
  buttons.push({
    label: strings.mainMenu,
    value: 'help',
  });
  return {
    id: `cart-error-${product.id || 'product'}-${now.getTime()}`,
    sender: 'ai',
    text: formatWidgetString(unconfirmed ? strings.cartAddUnconfirmed : strings.cartAddFailed, {
      item: String(product.title || '').trim() || strings.thatItem,
    }),
    timestamp: now.toISOString(),
    status: 'delivered',
    type: MESSAGE_TYPES.BUTTONS,
    richContent: {
      buttonStyle: 'pill',
      buttons,
    },
  };
}
